import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  CreditCard, Search, Download, Printer, ChevronLeft, Clock, CheckCircle, XCircle,
  Filter, ArrowUpDown, Eye, Receipt, Mail, RefreshCw, MapPin
} from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const PaymentsContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();
  
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchPayments(); }, [page, selectedSite]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/payments?page=${page}&limit=${limit}&status=completed`;
      if (search) url += `&reg_number=${encodeURIComponent(search)}`;
      if (selectedSite !== "all") url += `&site_id=${selectedSite}`;
      
      const res = await axios.get(url);
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Payment ID", "VRM", "Duration", "Amount", "Status", "Site", "Created", "Expires"];
    const rows = payments.map(p => [p.payment_id, p.reg_number, `${p.duration_hours}h`, p.amount_display, p.status, p.site_id || "", new Date(p.created_at).toLocaleString(), p.expires_at ? new Date(p.expires_at).toLocaleString() : ""]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "pending": return <Clock className="w-4 h-4 text-yellow-500" />;
      case "failed": return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-payments">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Completed Payments</h1><p className="text-sm text-gray-500">{total} completed payments</p></div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Printer className="w-4 h-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchPayments()} placeholder="Search by VRM, Payment ID, Email..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">VRM</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></td></tr>)
              : payments.length === 0 ? (<tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No payments found</td></tr>)
              : payments.map((payment) => (
                <tr key={payment.payment_id} onClick={() => navigate(`/admin/commercial/payments/${payment.payment_id}`)} className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-blue-500" />
                      <div><p className="font-medium text-gray-900 text-sm">{payment.payment_id}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="font-mono text-sm bg-yellow-100 px-2 py-1 rounded">{payment.reg_number}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-600">{payment.duration_hours}h</td>
                  <td className="px-4 py-3 text-sm font-bold text-green-600">{payment.amount_display}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(payment.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${payment.status === "completed" ? "bg-green-100 text-green-700" : payment.status === "pending" ? "bg-yellow-100 text-yellow-700" : payment.status === "failed" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{payment.status}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {payment.site_id ? (
                      <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" /> {payment.site_id.replace('site_', '').substring(0, 8)}</span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(payment.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => navigate(`/admin/commercial/payments/${payment.payment_id}`)} className="p-2 hover:bg-gray-100 rounded-lg" title="View"><Eye className="w-4 h-4 text-gray-500" /></button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg" title="Receipt"><Receipt className="w-4 h-4 text-blue-500" /></button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg" title="Email"><Mail className="w-4 h-4 text-purple-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
};

export default function CommercialPayments() {
  return (
    <CommercialProvider>
      <PaymentsContent />
    </CommercialProvider>
  );
}
